import { isSome } from "@sveabank/cc-common";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useApplicationDetails } from "../../../hooks/application";
import { usePackage } from "../../../hooks/package";
import { useApplicationId } from "../../../hooks/useApplicationId";
import { useSteps } from "../../../hooks/useSteps";
import {
	applicationsApiSlice,
	useAssignSignatorEmailsMutation,
	useFetchApplicationDetailsQuery,
} from "../../../services/application";
import {
	useAssignGuarantorMutation,
	useFetchGuarantorsQuery,
	useRemoveGuarantorMutation,
	useSetupGuarantorAgreementMutation,
} from "../../../services/guarantors";
import {
	useFetchPackageSignersQuery,
	useSetupPackageAgreementMutation,
} from "../../../services/package-agreement";
import { setPossibleGuarantors } from "../../../slices/guarantorSlice";
import type { AppDispatch } from "../../../store";
import type { GuarantorInteractive, GuarantorPost } from "../../../types/guarantor";
import { stepIs } from "../../../utils/application";
import { useApplicationSettings } from "../../application-settings/hooks";

export const useSignInformation = () => {
	const dispatch = useDispatch<AppDispatch>();
	const [agreementStepHasModalOpen, setAgreementStepHasModalOpen] = useState(false);
	const [guarantorStepHasModalOpen, setGuarantorStepHasModalOpen] = useState(false);
	const applicationId = useApplicationId();
	const { isCurrentUserApplicant, currentUserNationalId } = useApplicationDetails();

	const {
		isLoading: isStepsLoading,
		steps,
		refetchSteps,
		areStepsCompleted,
		someStepExists,
	} = useSteps();

	const [assignGuarantor] = useAssignGuarantorMutation();
	const [removeGuarantor] = useRemoveGuarantorMutation();
	const [setupPackageAgreement, { isLoading: isSetupPackageAgreementLoading }] =
		useSetupPackageAgreementMutation();
	const [setupGuarantorAgreement, { isLoading: isSetupGuarantorAgreementLoading }] =
		useSetupGuarantorAgreementMutation();
	const [assignSignatorEmails] = useAssignSignatorEmailsMutation();
	const { data: guarantors, refetch: refetchGuarantors } = useFetchGuarantorsQuery(applicationId);
	const { finalizeApplication } = useApplicationSettings();
	const { availablePackagesIsLoading, hasCreditProduct, selectedPackageData } = usePackage();

	const { data: signatories } = useFetchPackageSignersQuery(applicationId);
	const { data: applicationDetails, isLoading: isApplicationDetailsLoading } =
		useFetchApplicationDetailsQuery(applicationId);

	const { isOrgEf } = useApplicationDetails();
	const isLoading = isSome(isStepsLoading, isApplicationDetailsLoading, availablePackagesIsLoading);

	const isAssignGuarantorsCompleted = areStepsCompleted("AssignGuarantorStep");

	const isSignPackageCompleted =
		steps?.find(stepIs("SignPackageAgreementStep"))?.status === "Completed";
	const isSignGuarantorsCompleted =
		steps?.find(stepIs("SignGuarantorAgreementStep"))?.status === "Completed";

	const isSigningComplete =
		hasCreditProduct && !isOrgEf
			? isSignPackageCompleted && isSignGuarantorsCompleted
			: isSignPackageCompleted;

	const applicantContactInfo = steps?.find(stepIs("ApplicantContactInformationStep"))?.state;
	const organizationContactInfo = steps?.find(stepIs("OrganizationContactInformationStep"))?.state;
	const packageSignStatuses = steps?.find(stepIs("SignPackageAgreementStep"))?.state.signers || [];

	const guarantorsSignStatuses =
		steps?.find(stepIs("SignGuarantorAgreementStep"))?.state.signers || [];

	const hasCurrentUserSignedPackage = Boolean(
		packageSignStatuses?.find(
			(signer) => signer.nationalId === currentUserNationalId && signer.isSigned,
		),
	);
	const hasCurrentUserSignedGuarantors = Boolean(
		guarantorsSignStatuses?.find(
			(signer) => signer.nationalId === currentUserNationalId && signer.isSigned,
		),
	);

	const submitAssignedGuarantors = async (possibleGuarantors: GuarantorInteractive[]) => {
		for (const possibleGuarantor of possibleGuarantors) {
			const guarantor: GuarantorPost = {
				email: possibleGuarantor.email,
				guarantorType: possibleGuarantor.guarantorType,
				nationalId: possibleGuarantor.nationalId,
			};
			await assignGuarantor({ applicationId, guarantor }).unwrap();
		}
		// This is done to refresh the state list of added guarantors as this is not parts of steps.
		await refetchGuarantors().unwrap();
		dispatch(applicationsApiSlice.util.invalidateTags(["CurrentApplication"]));
	};

	const removeAllGuarantors = async () => {
		if (guarantors && guarantors?.length !== 0) {
			for (const oldGuarantor of guarantors) {
				await removeGuarantor({
					applicationId,
					swedishNationalId: oldGuarantor.nationalId,
				}).unwrap();
			}
		}
	};

	const handleGuarantorCheck = (nationalId: string, possibleGuarantors: GuarantorInteractive[]) => {
		const updatedPossibleGuarantors = possibleGuarantors.map((guarantor) =>
			guarantor.nationalId === nationalId
				? { ...guarantor, checked: !guarantor.checked }
				: guarantor,
		);
		dispatch(setPossibleGuarantors(updatedPossibleGuarantors));
	};

	const onRemoveGuarantor = (
		guarantor: GuarantorInteractive,
		possibleGuarantors: GuarantorInteractive[],
	) => {
		dispatch(
			setPossibleGuarantors(
				possibleGuarantors.filter((item) => item.nationalId !== guarantor.nationalId),
			),
		);
	};

	const setupAgreements = async () => {
		if (isOrgEf || !hasCreditProduct) {
			await setupPackageAgreement({ applicationId }).unwrap();
			dispatch(applicationsApiSlice.util.invalidateTags(["CurrentApplication"]));
		} else {
			await setupPackageAgreement({ applicationId }).unwrap();
			await setupGuarantorAgreement({ applicationId }).unwrap();
			dispatch(applicationsApiSlice.util.invalidateTags(["CurrentApplication"]));
		}
	};

	return {
		applicationDetails,
		hasCreditProduct,
		applicationId,
		applicantContactInfo,
		organizationContactInfo,
		selectedPackageData,
		isSigningComplete,
		isOrgEf,
		guarantorsSignStatuses,
		packageSignStatuses,
		refetchSteps,
		guarantors,
		submitAssignedGuarantors,
		setupAgreements,
		removeAllGuarantors,
		assignSignatorEmails,
		hasCurrentUserSignedPackage,
		hasCurrentUserSignedGuarantors,
		isAssignGuarantorsCompleted,
		isSignPackageCompleted,
		isSignGuarantorsCompleted,
		currentUserNationalId,
		isSetupPackageAgreementLoading,
		isSetupGuarantorAgreementLoading,
		isLoading,
		signatories,
		areStepsCompleted,
		isCurrentUserApplicant,
		someStepExists,
		handleGuarantorCheck,
		onRemoveGuarantor,
		finalizeApplication,
		agreementStepHasModalOpen,
		setAgreementStepHasModalOpen,
		guarantorStepHasModalOpen,
		setGuarantorStepHasModalOpen,
	};
};
